// src/portal-app/operations/EodScreen.js
//
// Plan 4 Task 19 — End of Day screen, migrated to the P4 pattern (spec §5).
// See operations/TransactionsScreen.js:32-55 for the pattern (and its file
// header) this follows; operations/ExpensesScreen.js (Task 6) is the
// closest reviewed model for a tab-free hero + CondensedRow list. This
// header only calls out decisions specific to EOD.
//
// No FilterButton/FilterSheet/SearchInput on this screen. Date selection is
// a business-day picker (a single fixed strip, not a range or a categorical
// filter), so it stays exactly what it was pre-migration — a horizontal
// chip strip above everything else. Adding a FilterButton here to match the
// P4 template would be the exact anti-pattern the brief calls out (Task 11
// / Devices declined the same way for the same reason).
//
// Hero — spec §7: "Hero summarises the day; sessions as condensed rows."
// Lead figure is `total_sales` under the EXISTING `total_sales` translation
// key/fallback ("Total Sales"), deliberately NOT "Today's Takings" — the
// selected date is very often NOT today (the date strip's whole purpose is
// picking a past business day), so a "today" label would misdescribe a
// historical figure. A `ShareBar` (cash/card) sits under it, same
// composition Transactions/Expenses use. A meta line below carries session
// counts, total expenses, flagged count and the store-level cash variance
// — the variance is rendered via `fmtVariance` (word + sign), never colour
// alone (see below).
//
// STRUCTURAL FIX — hero outside SurfaceStates (Plan 3 Task 2's usability
// bug, repeated on this screen pre-migration): the store-summary hero used
// to sit as a sibling of the INNER sessions `SurfaceStates` but there was
// no genuine 'empty' status for that inner block — `fetchDayData`
// unconditionally set `sessionsStatus('ready')` on success, so the
// `emptyTitle`/`emptyMsg` props it declared were dead code; a date with
// zero sessions still rendered an empty `eod-list`, not the empty branch.
// Fixed here in two parts: (1) `fetchSessions` now sets 'empty' when the
// date genuinely has zero sessions, giving the inner SurfaceStates a real
// empty branch to hit; (2) the hero is verified to survive both that empty
// branch AND a genuine fetch-error branch by a dedicated test with mutation
// evidence in task-19-report.md (move the hero inside the inner
// SurfaceStates, confirm the test FAILS, revert, confirm it passes again).
// This is possible without any data hack because `fetchSessions` and
// `fetchSummary` are now two INDEPENDENT fetches (see below) — the hero
// only depends on `summary`, which keeps its last-good value regardless of
// what happens to the session list fetch.
//
// Decoupled fetches, each with its own seqRef stale-response guard —
// pre-migration `fetchDayData` used a single `Promise.all([getSessionsByDate,
// getStoreSummary])` with ONE combined try/catch and NO seqRef at all (a
// slow response from a previous date flick could overwrite a fast one for
// the current date). Split into `fetchSessions(date)` and
// `fetchSummary(date)`, each carrying its own seqRef, called together
// whenever the selected date changes and together again on pull-to-refresh
// — same two service calls, same two arguments, just no longer coupled by a
// single Promise.all/catch pair. This is also what makes the hero-survival
// fix above possible: a session-list failure no longer takes the summary
// down with it.
//
// Rows — CondensedRow per till session: `name` = cashier (`employee_name`),
// `value` = `fmtMoney(total_sales)` (same denominator as the hero above),
// `pill` = status (`sessionTone`, same tone map as before — StatusBadge
// itself is retired from this screen since CondensedRow's pill IS the
// status chrome every other P4-migrated row uses; see TransactionsScreen's
// payment-method pill for the established precedent of a status-shaped
// value living in `pill` instead of a literal StatusBadge). `secondary` is
// composed (see `sessionSecondary` below) from: open/close time, cash sales,
// card sales, and — for closed/force_closed/expired sessions — the cash
// reconciliation ("Counted X / Y") and its variance. Force-closed sessions
// append the force-close reason. Nothing here is a fresh field: every part
// existed on the pre-migration SessionCard, just spread across a details
// grid instead of one line (see "Dropped fields" below for the one field
// that is NOT carried forward).
//
// Variance — never colour-only (spec §9, and this task's own explicit
// requirement). `fmtVariance` returns "+£40.00 over" / "-£20.00 short" /
// "Exact" — sign AND a word, so a £40 shortfall and a £40 surplus read
// differently even with colour vision deficiency or on a screen reader.
// Used for both the per-session variance (in `secondary`) and the
// store-level `cash-difference` figure in the hero meta line (testid
// preserved from the pre-migration screen).
//
// Collapsed-empty rule — this screen has no `FieldItem`/`GroupedRows`
// detail sections (that pattern is for editable profile screens like
// EmployeeDetailScreen; EOD sessions are read-only historical records with
// no "Add" action to offer, so a literal `EmptyFieldsPrompt` does not fit
// here — see its own file header: it names an onAdd-driven edit flow this
// screen doesn't have). The rule is applied instead the way every other P4
// row applies it: composing into ONE `secondary` string via
// `.filter(Boolean).join(' · ')`, so an unset field is silently omitted
// rather than rendered as its own dash/blank slot. The one place this
// screen previously had a THREE-field cluster that could all be unset at
// once — `end_cash`/`expected_cash`/`cash_difference`, all null together
// whenever a force-close used `not_counted: true` — collapses into a
// single reused-key sentence, "Cash was not counted" (the existing
// `cash_not_counted` key/fallback, already shipping as the checkbox
// label), instead of three silently-vanishing sub-fields that gave the
// manager no signal that reconciliation never happened at all.
//
// ConfirmSheet fix — REAL BUG, not a style preference. The pre-migration
// `ForceCloseSheet` rendered the not-counted checkbox and the close-error
// message in a hand-rolled `position: fixed` div AFTER `<ConfirmSheet>`,
// with a comment claiming "ConfirmSheet only accepts reasonLabel/
// reasonValue/onReasonChange as inputs." That was false for the CURRENT
// `shell/kit/ConfirmSheet` (Plan 4 Task 1) — it has accepted a `children`
// slot for exactly this since it moved, rendered INSIDE the real backdrop
// (see its own header: "anything rendered as a sibling of ConfirmSheet
// instead sits behind its fixed, z-indexed backdrop and is invisible in
// the live app" — precisely what the old fixed-div workaround did). Fixed
// by moving both into `<ConfirmSheet>...</ConfirmSheet>` children, exactly
// the pattern TransactionsScreen's void-error already uses. Plan 3 Task 1's
// `reason` → `reasonValue` fix on this screen's call site is untouched —
// still `reasonValue={reason}` (verified, never reintroduced `reason=`).
//
// Dropped fields (flagged explicitly, not silent): none beyond the
// pre-migration screen's own scope — `payment_summary`/`product_summary`/
// `denomination_count`/`notes` were never read by the pre-migration
// SessionCard either (see the file's original non-existent-fields test,
// preserved) and remain unread here.
//
// PullToRefresh (Task 2) wraps the sessions SurfaceStates only — the date
// strip and hero sit above it (outside the scroll container), same
// placement rule Expenses uses for its SegmentedControl + hero. Disabled
// while the force-close sheet is open.
//
// Fix round 1 (review finding) — the decoupled `fetchSummary` (see
// "Decoupled fetches" above) originally had a catch block that updated NO
// state at all: a `getStoreSummary` failure left `summary` at its previous
// value (null on first load) with zero indication anything had gone wrong
// and no way to retry — worse than the pre-migration screen, which flipped
// the WHOLE day view into an error+retry state on the same failure. Fixed
// by giving the hero its own `summaryStatus` ('loading'|'ready'|'error'),
// independent of `sessionsStatus`: on `summaryStatus === 'error'` the hero
// slot renders a small error card with its own retry button
// (`hero-retry-btn`) that re-fetches ONLY the summary — the sessions list
// stays completely untouched, preserving the whole point of the fetch
// split. Also fixed while in the file (minor): `.eod-not-counted`'s
// checkbox was an 18×18px target below the 44px touch-target guideline —
// the row is now a native `<label>` wrapping both the checkbox and its
// text, giving the entire row (not just the checkbox box) a >=44px-tall
// tap target.
//
// This screen renders CONTENT ONLY — NavStackHost owns the header.
import React, {
    useState, useEffect, useCallback, useRef,
} from 'react';
import { useAuth } from '../../context/AuthContext';
import { useLanguage } from '../../context/LanguageContext';
import { sessionService } from '../../services/api';
import { SurfaceStates, fmtMoney } from '../insights/kit';
import {
    ConfirmSheet, CondensedRow, ShareBar, PullToRefresh, ensureKitCss, IconCash,
} from '../shell/kit';
import { GlassCard } from '../shell/LiquidGlass';

/* ------------------------------------------------------------------ */
/* Screen-local stylesheet                                             */
/* ------------------------------------------------------------------ */
const CSS = `
/* ---- layout ---- */
.eod-wrap{width:100%;max-width:680px;margin:0 auto;box-sizing:border-box;
  padding:8px 0 80px;}

/* ---- date strip ---- */
.eod-dates{display:flex;gap:8px;overflow-x:auto;padding:2px 16px;margin-bottom:12px;
  -webkit-overflow-scrolling:touch;scrollbar-width:none;}
.eod-dates::-webkit-scrollbar{display:none;}
.eod-date-chip{flex:none;display:inline-flex;flex-direction:column;align-items:center;
  justify-content:center;min-height:56px;padding:6px 16px;border-radius:16px;
  cursor:pointer;font-family:inherit;white-space:nowrap;
  background:color-mix(in srgb,var(--c-surface) 38%,transparent);
  border:1.5px solid color-mix(in srgb,var(--c-surface) 55%,transparent);
  -webkit-backdrop-filter:blur(10px);backdrop-filter:blur(10px);
  transition:transform .12s ease,background .15s ease,border-color .15s ease;}
.eod-date-chip:active{transform:scale(.96);}
.eod-date-chip-active{background:var(--c-green);border-color:var(--c-green);}
.eod-date-chip-label{font-size:12px;font-weight:700;
  color:var(--c-ink);letter-spacing:.2px;}
.eod-date-chip-active .eod-date-chip-label{color:var(--c-white);}
.eod-date-chip-date{font-size:10px;font-weight:600;
  color:var(--c-textLight);margin-top:1px;}
.eod-date-chip-active .eod-date-chip-date{color:color-mix(in srgb,var(--c-white) 75%,transparent);}

/* ---- hero — the day's takings, ShareBar, meta line ---- */
.eod-hero{margin:0 16px 16px;padding:18px 20px 16px;}
.eod-hero-label{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:1.2px;color:var(--c-textLight);margin-bottom:7px;}
.eod-hero-value{font-size:38px;font-weight:800;letter-spacing:-1.2px;
  color:var(--c-ink);line-height:1.05;margin-bottom:12px;}
.eod-hero-value:last-child{margin-bottom:0;}
.eod-hero-meta{display:flex;flex-wrap:wrap;gap:8px 16px;margin-top:12px;}
.eod-hero-meta-item{font-size:12px;font-weight:600;color:var(--c-textLight);}
.eod-hero-flagged{color:var(--c-amber);}
.eod-diff-green{color:var(--c-success);}
.eod-diff-red{color:var(--c-red);}

/* ---- hero error state — surfaced, not silent (Fix round 1) ---- */
.eod-hero-error{display:flex;align-items:center;justify-content:space-between;
  gap:12px;}
.eod-hero-error-text{font-size:13px;font-weight:600;color:var(--c-red);}
.eod-hero-retry{min-height:44px;padding:0 16px;border:1.5px solid var(--c-red);
  border-radius:12px;background:color-mix(in srgb,var(--c-red) 8%,transparent);
  font-family:inherit;font-size:13px;font-weight:700;color:var(--c-red);
  cursor:pointer;white-space:nowrap;flex:none;
  transition:background .15s ease,transform .12s ease;}
.eod-hero-retry:active{transform:scale(.97);}

/* ---- section headers ---- */
.eod-section{margin:0 16px 8px;}
.eod-section-title{font-size:11px;font-weight:700;text-transform:uppercase;
  letter-spacing:.8px;color:var(--c-textLight);margin-bottom:8px;
  display:flex;align-items:center;gap:6px;}
.eod-section-badge{display:inline-flex;align-items:center;justify-content:center;
  min-width:20px;height:20px;border-radius:999px;font-size:10px;font-weight:700;
  background:color-mix(in srgb,var(--c-ink) 8%,transparent);color:var(--c-ink);}

/* ---- session list ---- */
.eod-list{display:flex;flex-direction:column;gap:8px;margin:0 16px 16px;}
.eod-row-wrap{display:flex;flex-direction:column;gap:4px;}

/* ---- flagged tag — text label, never colour alone ---- */
.eod-flag-tag{display:inline-flex;align-items:center;gap:4px;align-self:flex-start;
  margin-inline-start:44px;font-size:11px;font-weight:700;color:var(--c-amber);}
.eod-flag-tag svg{width:12px;height:12px;}

/* ---- force-close button ---- */
.eod-force-btn{min-height:44px;padding:0 14px;border:1.5px solid var(--c-red);
  border-radius:12px;background:color-mix(in srgb,var(--c-red) 8%,transparent);
  font-family:inherit;font-size:12px;font-weight:700;color:var(--c-red);
  cursor:pointer;white-space:nowrap;flex:none;align-self:flex-start;
  margin-inline-start:44px;
  transition:background .15s ease,transform .12s ease;}
.eod-force-btn:active{transform:scale(.97);}

/* ---- not-counted checkbox row (rendered inside ConfirmSheet's children) ----
   The row itself IS the <label> (Fix round 1, minor) — min-height:44px
   gives the whole clickable row a proper touch target, not just the 18px
   checkbox box. */
.eod-not-counted{display:flex;align-items:center;gap:8px;min-height:44px;
  cursor:pointer;}
.eod-not-counted input[type="checkbox"]{width:18px;height:18px;flex:none;
  cursor:pointer;accent-color:var(--c-red);}
.eod-not-counted span{font-size:13px;font-weight:600;color:var(--c-textLight);}

/* ---- confirm error (rendered inside ConfirmSheet's children) ---- */
.eod-confirm-error{font-size:12px;font-weight:600;color:var(--c-red);
  margin-top:6px;padding:0 4px;}

/* ---- tabular numerals ---- */
.eod-num{font-variant-numeric:tabular-nums;}

@media (prefers-reduced-motion: reduce){
  .eod-date-chip,.eod-date-chip:active,
  .eod-force-btn,.eod-force-btn:active,
  .eod-hero-retry,.eod-hero-retry:active{transition:none;transform:none;}
}
`;

let cssInjected = false;
function ensureCss() {
    if (cssInjected || typeof document === 'undefined') return;
    const tag = document.createElement('style');
    tag.id = 'eod-css';
    tag.textContent = CSS;
    document.head.appendChild(tag);
    cssInjected = true;
}

/* ------------------------------------------------------------------ */
/* Inline SVG icons (no emoji).                                        */
/* ------------------------------------------------------------------ */
const FlagIcon = () => (
    <svg
        width="14"
        height="14"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
        aria-hidden="true"
    >
        <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
        <line x1="4" y1="22" x2="4" y2="15" />
    </svg>
);

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

/** Format local time from an ISO string — never toISOString(). */
function fmtLocalTime(iso) {
    if (!iso) return '';
    try {
        const d = new Date(iso);
        const h = d.getHours();
        const m = String(d.getMinutes()).padStart(2, '0');
        const period = h >= 12 ? 'PM' : 'AM';
        const h12 = h % 12 || 12;
        return `${h12}:${m} ${period}`;
    } catch (_) {
        return '';
    }
}

/**
 * Format a YYYY-MM-DD string to a relative label:
 * "today", "yesterday", or "Mon 28 Jul".
 * Never uses toISOString() — parses via local midnight constructor.
 * Day names are localised via t().
 */
function fmtDateLabel(dateStr, t) {
    if (!dateStr) return dateStr;
    // Parse as local midnight
    const d = new Date(`${dateStr}T00:00:00`);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const yesterday = new Date(today);
    yesterday.setDate(today.getDate() - 1);

    const dMidnight = new Date(d);
    dMidnight.setHours(0, 0, 0, 0);

    if (dMidnight.getTime() === today.getTime()) return 'today';
    if (dMidnight.getTime() === yesterday.getTime()) return 'yesterday';
    // "Mon 28 Jul" — day name is localised
    const dayOfWeek = d.getDay();
    const dayKeys = ['day_sun', 'day_mon', 'day_tue', 'day_wed', 'day_thu', 'day_fri', 'day_sat'];
    const dayLabel = t(dayKeys[dayOfWeek], {}, ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][dayOfWeek]);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${dayLabel} ${d.getDate()} ${months[d.getMonth()]}`;
}

/** Format the short date portion — e.g. "1 Aug" */
function fmtShortDate(dateStr) {
    if (!dateStr) return '';
    const d = new Date(`${dateStr}T00:00:00`);
    const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun',
        'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    return `${d.getDate()} ${months[d.getMonth()]}`;
}

/** Dual-alias flagged check. `is_flagged` never actually exists on
 *  SessionResponse (verified against backend/app/models/cashier_session.py
 *  — only `flagged: bool` is defined) but the check is defensive and
 *  harmless, so it is kept unchanged from the pre-migration screen. */
function isFlagged(session) {
    return !!(session.flagged || session.is_flagged);
}

/** Resolve icon/pill tone for a session status. */
function sessionTone(status) {
    switch (status) {
        case 'open': return 'blue';
        case 'closed': return 'green';
        case 'force_closed': return 'amber';
        case 'expired': return 'muted';
        default: return 'muted';
    }
}

/** "+£40.00 over" / "-£20.00 short" / "Exact" — sign AND a word, never
 *  colour alone (spec §9, and this task's explicit variance requirement). */
function fmtVariance(diff, currency, t) {
    if (diff == null) return null;
    const n = Number(diff);
    if (n === 0) return t('variance_exact', {}, 'Exact');
    const amt = fmtMoney(Math.abs(n), currency);
    return n > 0
        ? t('variance_over', { amount: amt }, '+{amount} over')
        : t('variance_short', { amount: amt }, '-{amount} short');
}

/** Composes a session row's secondary line. See file header
 *  "Collapsed-empty rule" for why the reconciliation cluster
 *  (end_cash/expected_cash/cash_difference all null) collapses into one
 *  sentence instead of three silently-vanishing sub-fields. */
function sessionSecondary(session, currency, t) {
    const isOpen = session.status === 'open';
    const parts = [];

    parts.push(
        !isOpen && session.end_time
            ? `${fmtLocalTime(session.start_time)}–${fmtLocalTime(session.end_time)}`
            : `${t('session_start', {}, 'Started')} ${fmtLocalTime(session.start_time)}`,
    );

    if (session.cash_sales != null) {
        parts.push(`${t('cash', {}, 'Cash')} ${fmtMoney(session.cash_sales, currency)}`);
    }
    if (session.card_sales != null) {
        parts.push(`${t('card', {}, 'Card')} ${fmtMoney(session.card_sales, currency)}`);
    }

    if (!isOpen) {
        const hasCount = session.end_cash != null && session.expected_cash != null;
        if (hasCount) {
            parts.push(`${t('counted', {}, 'Counted')} ${fmtMoney(session.end_cash, currency)} / ${fmtMoney(session.expected_cash, currency)}`);
            const variance = fmtVariance(session.cash_difference, currency, t);
            if (variance) parts.push(variance);
        } else {
            // Collapsed-empty: end_cash/expected_cash/cash_difference are ALL
            // null together (a not_counted force-close) — one sentence, not
            // three silently-missing sub-fields.
            parts.push(t('cash_not_counted', {}, 'Cash was not counted'));
        }
    }

    if (session.status === 'force_closed' && session.force_close_reason) {
        parts.push(`${t('force_closed_by', {}, 'Force closed')}: ${session.force_close_reason}`);
    }

    return parts.filter(Boolean).join(' · ');
}

/* ------------------------------------------------------------------ */
/* NotCountedRow — checkbox rendered inside ConfirmSheet's children.    */
/* The whole row is a native <label> (not a separate htmlFor/id pair)   */
/* so the entire row — not just the visually small checkbox box — is a  */
/* single >=44px-tall tap target (Fix round 1, minor). Clicking anywhere */
/* on the row toggles the checkbox; the checkbox itself keeps its usual */
/* small visual size. */
/* ------------------------------------------------------------------ */
function NotCountedRow({ checked, onChange, t }) {
    return (
        <label className="eod-not-counted">
            <input
                type="checkbox"
                data-testid="not-counted-checkbox"
                checked={checked}
                onChange={(e) => onChange(e.target.checked)}
                onClick={(e) => {
                    // onClick fires before React's controlled re-render, so
                    // e.target.checked reflects the NEW desired state.
                    onChange(e.target.checked);
                }}
            />
            <span>{t('cash_not_counted', {}, 'Cash was not counted')}</span>
        </label>
    );
}

/* ------------------------------------------------------------------ */
/* StoreSummaryHero — the day's takings (spec §7). Rendered OUTSIDE the */
/* sessions SurfaceStates — see file header, "STRUCTURAL FIX".          */
/* ------------------------------------------------------------------ */
function StoreSummaryHero({ summary, currency, t }) {
    const cashValue = Number(summary.total_cash) || 0;
    const cardValue = Number(summary.total_card) || 0;
    const showShareBar = (cashValue + cardValue) > 0;
    const shareSegments = [
        { id: 'cash', label: t('cash', {}, 'Cash'), value: cashValue, display: fmtMoney(cashValue, currency), tone: 'green' },
        { id: 'card', label: t('card', {}, 'Card'), value: cardValue, display: fmtMoney(cardValue, currency), tone: 'blue' },
    ];
    const shareAriaLabel = t(
        'cash_card_split_aria',
        { cash: fmtMoney(cashValue, currency), card: fmtMoney(cardValue, currency) },
        'Cash {cash}, Card {card}',
    );

    const hasCashDiff = summary.cash_difference != null && summary.cash_difference !== 0;
    const diffTone = summary.cash_difference > 0 ? 'green' : 'red';
    const varianceText = fmtVariance(summary.cash_difference, currency, t);

    return (
        <GlassCard veil className="eod-hero" data-testid="store-summary">
            <div className="eod-hero-label">{t('total_sales', {}, 'Total Sales')}</div>
            <div className="eod-hero-value eod-num" data-testid="hero-revenue">
                {fmtMoney(summary.total_sales, currency)}
            </div>
            {showShareBar && (
                <ShareBar segments={shareSegments} ariaLabel={shareAriaLabel} />
            )}
            <div className="eod-hero-meta">
                <span className="eod-hero-meta-item eod-num">
                    {summary.active_sessions} {t('open', {}, 'open')} / {summary.closed_sessions} {t('done', {}, 'done')}
                </span>
                <span className="eod-hero-meta-item eod-num">
                    {t('total_expenses', {}, 'Expenses')} {fmtMoney(summary.total_expenses, currency)}
                </span>
                {summary.flagged_count > 0 && (
                    <span className="eod-hero-meta-item eod-hero-flagged eod-num" data-testid="flagged-count">
                        {summary.flagged_count} {t('flagged', {}, 'flagged')}
                    </span>
                )}
                {hasCashDiff && (
                    <span
                        className={`eod-hero-meta-item eod-num eod-diff-${diffTone}`}
                        data-testid="cash-difference"
                        data-tone={diffTone}
                    >
                        {varianceText}
                    </span>
                )}
            </div>
        </GlassCard>
    );
}

/* ------------------------------------------------------------------ */
/* SessionRow — CondensedRow plus the flagged tag / force-close button  */
/* beneath it, same wrapper pattern Transactions/Expenses use.          */
/* ------------------------------------------------------------------ */
function SessionRow({ session, currency, t, onForceClose }) {
    const flagged = isFlagged(session);
    const isOpen = session.status === 'open';

    return (
        <div className="eod-row-wrap" data-testid={`session-row-${session.session_id}`}>
            {flagged && (
                <span
                    className="eod-flag-tag"
                    data-testid="flagged-indicator"
                    title={t('flagged', {}, 'Flagged')}
                    aria-label={t('flagged', {}, 'Flagged')}
                >
                    <FlagIcon />
                    {t('flagged', {}, 'Flagged')}
                </span>
            )}
            <CondensedRow
                icon={<IconCash />}
                tone={sessionTone(session.status)}
                name={session.employee_name}
                value={fmtMoney(session.total_sales, currency)}
                secondary={sessionSecondary(session, currency, t) || undefined}
                pill={{
                    label: t(`status_${session.status}`, {}, session.status.replace(/_/g, ' ')),
                    tone: sessionTone(session.status),
                }}
                testId={session.session_id}
            />
            {isOpen && onForceClose && (
                <button
                    type="button"
                    className="eod-force-btn"
                    data-testid="force-close-btn"
                    onClick={onForceClose}
                >
                    {t('force_close', {}, 'Force Close')}
                </button>
            )}
        </div>
    );
}

/* ------------------------------------------------------------------ */
/* ForceCloseSheet                                                      */
/* ------------------------------------------------------------------ */
function ForceCloseSheet({
    session,
    isLast,
    reason,
    onReasonChange,
    notCounted,
    onNotCountedChange,
    loading,
    error,
    onClose,
    onConfirm,
    t,
}) {
    const open = Boolean(session);

    if (!open) return null;

    const empName = session ? session.employee_name : '';

    const standardMsg = `${empName}'s ${t('force_close_confirm_msg', {}, 'till will be force-closed and flagged.')}`;
    const lastMsg = t(
        'end_business_day_warning',
        {},
        'This is the last open till. Closing it will END today\'s business day. POS cashiers will be returned to the start-of-day screen. This cannot be undone.',
    );

    return (
        <ConfirmSheet
            open={open}
            onClose={onClose}
            onConfirm={onConfirm}
            title={
                isLast
                    ? t('end_business_day', {}, 'End Business Day')
                    : t('force_close_till', {}, 'Force Close Till')
            }
            message={isLast ? lastMsg : standardMsg}
            confirmLabel={
                isLast
                    ? t('end_day_confirm', {}, 'End Business Day')
                    : t('force_close', {}, 'Force Close')
            }
            destructive
            loading={loading}
            requiredPhrase={isLast ? 'END DAY' : undefined}
            phraseHint={
                isLast
                    ? t('type_end_day', {}, 'Type END DAY to confirm ending the business day')
                    : undefined
            }
            reasonLabel={
                !isLast ? t('force_close_reason', {}, 'Force close reason') : undefined
            }
            reasonValue={reason}
            onReasonChange={onReasonChange}
        >
            {/* Rendered INSIDE ConfirmSheet's children slot — see file header
                "ConfirmSheet fix". Anything rendered as a sibling of
                ConfirmSheet instead sits behind its fixed, z-indexed
                backdrop and is invisible in the live app. */}
            <NotCountedRow
                checked={notCounted}
                onChange={onNotCountedChange}
                t={t}
            />
            {error && (
                <div className="eod-confirm-error" data-testid="confirm-error">
                    {error}
                </div>
            )}
        </ConfirmSheet>
    );
}

/* ------------------------------------------------------------------ */
/* EodScreen                                                            */
/* ------------------------------------------------------------------ */
export default function EodScreen() {
    ensureCss();
    ensureKitCss();
    const { getCurrency } = useAuth();
    const { t } = useLanguage();
    const currency = getCurrency();

    // ── Business date state ───────────────────────────────────────────
    const [datesStatus, setDatesStatus] = useState('loading'); // loading|error|empty|ready
    const [businessDates, setBusinessDates] = useState([]);
    const [selectedDate, setSelectedDate] = useState(null);
    const datesSeqRef = useRef(0);

    // ── Sessions state (the list) ───────────────────────────────────────
    const [sessionsStatus, setSessionsStatus] = useState('loading');
    const [sessions, setSessions] = useState([]);
    const sessionsSeqRef = useRef(0);

    // ── Summary state (the hero) — independent of sessionsStatus, see     ──
    // file header "Decoupled fetches" for why. `summaryStatus` is its own
    // small state machine (loading|ready|error) so a summary-only failure
    // is visible and recoverable on its own — see file header "Fix round 1".
    const [summary, setSummary] = useState(null);
    const [summaryStatus, setSummaryStatus] = useState('loading');
    const summarySeqRef = useRef(0);

    // ── Force-close state ─────────────────────────────────────────────
    const [confirmTarget, setConfirmTarget] = useState(null); // session being closed
    const [confirmIsLast, setConfirmIsLast] = useState(false);
    const [closeReason, setCloseReason] = useState('');
    const [notCounted, setNotCounted] = useState(false);
    const [closeLoading, setCloseLoading] = useState(false);
    const [closeError, setCloseError] = useState('');

    // ── Fetch business dates ──────────────────────────────────────────
    const fetchDates = useCallback(async () => {
        const seq = ++datesSeqRef.current;
        setDatesStatus('loading');
        try {
            const dates = await sessionService.getBusinessDates();
            if (seq !== datesSeqRef.current) return;
            setBusinessDates(dates || []);
            if (dates && dates.length > 0) {
                setSelectedDate(dates[0]);
                setDatesStatus('ready');
            } else {
                setSelectedDate(null);
                setDatesStatus('empty');
            }
        } catch (_err) {
            if (seq !== datesSeqRef.current) return;
            setDatesStatus('error');
        }
    }, []);

    useEffect(() => { fetchDates(); }, [fetchDates]);

    // ── Fetch sessions for the selected date (the list) ────────────────
    const fetchSessions = useCallback(async (date) => {
        if (!date) return;
        const seq = ++sessionsSeqRef.current;
        setSessionsStatus('loading');
        try {
            const items = await sessionService.getSessionsByDate(date);
            if (seq !== sessionsSeqRef.current) return;
            const list = items || [];
            setSessions(list);
            setSessionsStatus(list.length === 0 ? 'empty' : 'ready');
        } catch (_err) {
            if (seq !== sessionsSeqRef.current) return;
            setSessionsStatus('error');
        }
    }, []);

    // ── Fetch store summary for the selected date (the hero) ───────────
    const fetchSummary = useCallback(async (date) => {
        if (!date) return;
        const seq = ++summarySeqRef.current;
        setSummaryStatus('loading');
        try {
            const result = await sessionService.getStoreSummary(date);
            if (seq !== summarySeqRef.current) return;
            setSummary(result || null);
            setSummaryStatus('ready');
        } catch (_err) {
            if (seq !== summarySeqRef.current) return;
            // Surfaced, not swallowed — see file header "Fix round 1". A
            // summary-only failure (plausible on cellular against a
            // cold-starting Cloud Run backend) must be visible and
            // recoverable, not a hero that's just quietly missing its
            // headline number while everything else looks fine. The
            // sessions list stays completely independent of this status.
            setSummaryStatus('error');
        }
    }, []);

    useEffect(() => {
        if (selectedDate && datesStatus === 'ready') {
            fetchSessions(selectedDate);
            fetchSummary(selectedDate);
        }
    }, [selectedDate, datesStatus, fetchSessions, fetchSummary]);

    const refreshDay = useCallback(async () => {
        if (!selectedDate) return;
        await Promise.all([fetchSessions(selectedDate), fetchSummary(selectedDate)]);
    }, [selectedDate, fetchSessions, fetchSummary]);

    // ── Open force-close confirmation ─────────────────────────────────
    const openForceClose = useCallback((session) => {
        const openCount = sessions.filter((s) => s.status === 'open').length;
        const isLast = openCount === 1;
        setConfirmTarget(session);
        setConfirmIsLast(isLast);
        setCloseReason('');
        setNotCounted(false);
        setCloseError('');
    }, [sessions]);

    const closeSheet = useCallback(() => {
        setConfirmTarget(null);
        setCloseReason('');
        setNotCounted(false);
        setCloseError('');
        setCloseLoading(false);
    }, []);

    // ── Execute force-close ───────────────────────────────────────────
    const handleForceClose = useCallback(async () => {
        if (!confirmTarget) return;
        setCloseLoading(true);
        setCloseError('');
        try {
            await sessionService.forceCloseSession(confirmTarget.session_id, {
                reason: closeReason,
                not_counted: notCounted,
            });
            closeSheet();
            // Reload sessions + summary for the selected date, and refresh
            // the business-dates strip (a force-close can end the day).
            await Promise.all([fetchSessions(selectedDate), fetchSummary(selectedDate)]);
            await fetchDates();
        } catch (err) {
            setCloseError(err?.message || t('force_close_error', {}, 'Force close failed'));
            setCloseLoading(false);
        }
    }, [confirmTarget, closeReason, notCounted, closeSheet, fetchSessions, fetchSummary, selectedDate, fetchDates, t]);

    // ── Partition sessions ────────────────────────────────────────────
    const openSessions = sessions.filter((s) => s.status === 'open');
    const closedSessions = sessions.filter((s) => s.status !== 'open');

    // ── Render ────────────────────────────────────────────────────────

    // Date strip — OUTSIDE SurfaceStates so you can still switch dates
    // after an error or from an empty list.
    const dateStrip = datesStatus !== 'loading' && businessDates.length > 0 ? (
        <div className="eod-dates" role="group" aria-label={t('business_date', {}, 'Business date')}>
            {businessDates.map((d) => {
                const active = d === selectedDate;
                const label = fmtDateLabel(d, t);
                const short = label === 'today' || label === 'yesterday'
                    ? fmtShortDate(d)
                    : '';
                return (
                    <button
                        key={d}
                        type="button"
                        className={`eod-date-chip${active ? ' eod-date-chip-active' : ''}`}
                        data-testid={`date-chip-${d}`}
                        aria-pressed={String(active)}
                        onClick={() => { if (!active) setSelectedDate(d); }}
                    >
                        <span className="eod-date-chip-label">
                            {label === 'today'
                                ? t('today', {}, 'Today')
                                : label === 'yesterday'
                                    ? t('yesterday', {}, 'Yesterday')
                                    : label}
                        </span>
                        {short && (
                            <span className="eod-date-chip-date">{short}</span>
                        )}
                    </button>
                );
            })}
        </div>
    ) : null;

    return (
        <div className="eod-wrap">
            {dateStrip}

            <SurfaceStates
                status={datesStatus}
                onRetry={fetchDates}
                emptyTitle={t('no_business_days', {}, 'No business days yet')}
                emptyMsg={t('no_business_days_msg', {}, 'Start your first business day from the POS.')}
            >
                {/* Hero — OUTSIDE the sessions SurfaceStates below, so it
                    survives a genuine empty/error result on the session-list
                    fetch. See file header "STRUCTURAL FIX" and
                    task-19-report.md for mutation evidence. Its own
                    `summaryStatus` (independent of `sessionsStatus`) gives
                    a `getStoreSummary` failure a visible, recoverable error
                    state instead of silently leaving the hero missing — see
                    file header "Fix round 1". Retry re-fetches ONLY the
                    summary; the sessions list is untouched. */}
                {summaryStatus === 'error' ? (
                    <GlassCard veil className="eod-hero eod-hero-error" data-testid="hero-error">
                        <div className="eod-hero-error-text">
                            {t('hero_load_error', {}, "Couldn't load today's summary.")}
                        </div>
                        <button
                            type="button"
                            className="eod-hero-retry"
                            data-testid="hero-retry-btn"
                            onClick={() => fetchSummary(selectedDate)}
                        >
                            {t('retry', {}, 'Retry')}
                        </button>
                    </GlassCard>
                ) : summary ? (
                    <StoreSummaryHero summary={summary} currency={currency} t={t} />
                ) : null}

                <PullToRefresh onRefresh={refreshDay} disabled={!!confirmTarget}>
                    <SurfaceStates
                        status={sessionsStatus}
                        onRetry={() => fetchSessions(selectedDate)}
                        emptyTitle={t('no_sessions', {}, 'No sessions for this date')}
                        emptyMsg={t('no_sessions_msg', {}, 'No till sessions were recorded for this date.')}
                    >
                        {/* Active sessions */}
                        <div className="eod-section" data-testid="active-sessions">
                            <div className="eod-section-title">
                                {t('open_sessions', {}, 'Open Sessions')}
                                <span className="eod-section-badge">{openSessions.length}</span>
                            </div>
                            {openSessions.length > 0 ? (
                                <div className="eod-list">
                                    {openSessions.map((s) => (
                                        <SessionRow
                                            key={s.session_id}
                                            session={s}
                                            currency={currency}
                                            t={t}
                                            onForceClose={() => openForceClose(s)}
                                        />
                                    ))}
                                </div>
                            ) : (
                                <GlassCard veil style={{ margin: '0 0 8px', padding: '12px 16px' }}>
                                    <span style={{ fontSize: 13, color: 'var(--c-textLight)' }}>
                                        {t('no_open_sessions', {}, 'All tills are closed')}
                                    </span>
                                </GlassCard>
                            )}
                        </div>

                        {/* Closed sessions */}
                        {closedSessions.length > 0 && (
                            <div className="eod-section" data-testid="closed-sessions">
                                <div className="eod-section-title">
                                    {t('closed_sessions', {}, 'Closed Sessions')}
                                    <span className="eod-section-badge">{closedSessions.length}</span>
                                </div>
                                <div className="eod-list">
                                    {closedSessions.map((s) => (
                                        <SessionRow
                                            key={s.session_id}
                                            session={s}
                                            currency={currency}
                                            t={t}
                                            onForceClose={null}
                                        />
                                    ))}
                                </div>
                            </div>
                        )}
                    </SurfaceStates>
                </PullToRefresh>
            </SurfaceStates>

            {/* Force-close confirmation sheet */}
            <ForceCloseSheet
                session={confirmTarget}
                isLast={confirmIsLast}
                reason={closeReason}
                onReasonChange={setCloseReason}
                notCounted={notCounted}
                onNotCountedChange={setNotCounted}
                loading={closeLoading}
                error={closeError}
                onClose={closeSheet}
                onConfirm={handleForceClose}
                t={t}
            />
        </div>
    );
}
